import { createElement, useState } from "react";
import { TextInput, type TextInputProps } from "react-native";
import type { LucideIcon } from "lucide-react-native";
import {
  clampTime,
  formatTimeTyping,
  isValidTime,
} from "@src/packages/utils/time";
import { colors, typography } from "../theme/tokens";
import { useInputFontSize } from "../utils/inputFontSize";
import { MoonFormField } from "./MoonFormField";
import { MoonFieldInput } from "./MoonFieldInput";

function isMobileWeb(): boolean {
  return (
    typeof window !== "undefined" &&
    typeof window.matchMedia === "function" &&
    window.matchMedia("(pointer: coarse)").matches
  );
}

type Props = {
  label: string;
  /** Decorative Lucide icon rendered immediately before the field label. */
  labelIcon?: LucideIcon;
  value: string;
  onChange: (next: string) => void;
  placeholder?: string;
  error?: string;
  hint?: string;
  required?: boolean;
  disabled?: boolean;
  loading?: boolean;
  textInputProps?: Omit<
    TextInputProps,
    | "value"
    | "onChangeText"
    | "onFocus"
    | "onBlur"
    | "editable"
    | "keyboardType"
    | "placeholder"
    | "placeholderTextColor"
    | "style"
  >;
  testID?: string;
};

export function MoonTimeField({
  label,
  labelIcon,
  value,
  onChange,
  placeholder = "HH:MM",
  error,
  hint,
  required,
  disabled,
  loading,
  textInputProps,
  testID,
}: Props) {
  const fontSize = useInputFontSize();
  const [focused, setFocused] = useState(false);
  const [buffer, setBuffer] = useState(value);

  if (!focused && buffer !== value) {
    setBuffer(value);
  }

  const handleChangeText = (text: string) => {
    const formatted = formatTimeTyping(text);
    setBuffer(formatted);

    if (isValidTime(formatted)) {
      onChange(formatted);
    } else if (formatted === "") {
      onChange("");
    }
  };

  const handleBlur = () => {
    setFocused(false);
    if (buffer === "") {
      onChange("");
      return;
    }
    const clamped = clampTime(buffer);
    if (clamped === null) {
      setBuffer("");
      onChange("");
    } else {
      setBuffer(clamped);
      if (clamped !== value) onChange(clamped);
    }
  };

  return (
    <MoonFormField
      label={label}
      labelIcon={labelIcon}
      error={error}
      hint={hint}
      required={required}
      disabled={disabled}
      loading={loading}
      testID={testID}
    >
      <MoonFieldInput hasError={!!error} focused={focused} paddingY={12}>
        {isMobileWeb() ? (
          createElement("input", {
            type: "time",
            value: buffer,
            onChange: (e: React.ChangeEvent<HTMLInputElement>) =>
              handleChangeText(e.target.value),
            onFocus: () => setFocused(true),
            onBlur: handleBlur,
            disabled,
            placeholder,
            style: {
              fontFamily: typography.families.sans,
              fontSize,
              color: colors.text,
              border: "none",
              outline: "none",
              background: "transparent",
              width: "100%",
              textAlign: "left" as const,
              WebkitAppearance: "none" as const,
              appearance: "none" as const,
            },
          })
        ) : (
          <TextInput
            value={buffer}
            onChangeText={handleChangeText}
            onFocus={() => setFocused(true)}
            onBlur={handleBlur}
            editable={!disabled}
            keyboardType="number-pad"
            placeholder={placeholder}
            placeholderTextColor={colors.textFaint}
            maxLength={5}
            {...textInputProps}
            style={{
              fontFamily: typography.families.sans,
              fontSize,
              color: colors.text,
            }}
          />
        )}
      </MoonFieldInput>
    </MoonFormField>
  );
}
